// POS Screen – optimized for desktop, tablet, and mobile greenhouse checkout
import { useState, useCallback } from "react";
import {
	Box,
	Typography,
	Paper,
	Button,
	IconButton,
	Divider,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TextField,
	Select,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Chip,
	CircularProgress,
	Alert,
	Snackbar,
} from "@mui/material";
import { Add, Remove, Delete, CheckCircle } from "@mui/icons-material";
import supabase from "../../supabaseClient";
import BarcodeScanner from "../../Components/BarcodeScanner";
import useAdminAuth from "../../utils/useAdminAuth";
import AuthGate from "../../Components/AuthGate";

const POS_LOOKUP_API = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/pos-lookup-barcode`;
const POS_COMPLETE_API = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/pos-complete-sale`;

const POS = () => {
	const { authChecked, isLoggedIn, isAdmin } = useAdminAuth();

	if (!authChecked) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!isLoggedIn || !isAdmin) {
		return <AuthGate variant="unauthorized" />;
	}

	return <POSContent />;
};

const POSContent = () => {
	const [cart, setCart] = useState([]); // [{store_item_id, title, price_cents, quantity, quantity_on_hand}]
	const [paymentMethod, setPaymentMethod] = useState("cash");
	const [lookupError, setLookupError] = useState("");
	const [lookupLoading, setLookupLoading] = useState(false);
	const [completing, setCompleting] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [successSnack, setSuccessSnack] = useState(false);
	const [errorSnack, setErrorSnack] = useState("");
	const [lastSaleId, setLastSaleId] = useState(null);
	// Tracks the last item added via camera scan – used for the scanner-dialog undo button
	const [lastScannedItem, setLastScannedItem] = useState(null); // { store_item_id, title }
	// Increments on every successful scan so BarcodeScanner can re-trigger the alert
	// even when the same item is scanned multiple times in a row
	const [scanKey, setScanKey] = useState(0);

	// Manual search state
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [searching, setSearching] = useState(false);

	const lookupBarcode = useCallback(async (value) => {
		setLookupError("");
		setLookupLoading(true);
		try {
			const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
			const authHeader = "Bearer " + anonKey;
			const res = await fetch(
				`${POS_LOOKUP_API}?barcode=${encodeURIComponent(value)}`,
				{ headers: { Authorization: authHeader } },
			);
			const data = await res.json();
			if (!res.ok || !data.item) {
				// Try SKU lookup
				const res2 = await fetch(
					`${POS_LOOKUP_API}?sku=${encodeURIComponent(value)}`,
					{ headers: { Authorization: authHeader } },
				);
				const data2 = await res2.json();
				if (!res2.ok || !data2.item) {
					setLookupError(`Unknown barcode: "${value}"`);
					setLookupLoading(false);
					return;
				}
				addToCart(data2.item);
				if (data2.item.quantity_on_hand > 0) {
					setLastScannedItem({
						store_item_id: data2.item.id,
						title: data2.item.title,
					});
					setScanKey((k) => k + 1);
				}
			} else {
				addToCart(data.item);
				if (data.item.quantity_on_hand > 0) {
					setLastScannedItem({
						store_item_id: data.item.id,
						title: data.item.title,
					});
					setScanKey((k) => k + 1);
				}
			}
		} catch (err) {
			setLookupError("Lookup failed: " + err.message);
		}
		setLookupLoading(false);
	}, []);

	const addToCart = (item) => {
		setCart((prev) => {
			const existing = prev.find((c) => c.store_item_id === item.id);
			if (existing) {
				//disallow adding more if it would exceed stock
				/*
				if (existing.quantity >= item.quantity_on_hand) {
					setLookupError(`Max stock reached for "${item.title}" (${item.quantity_on_hand} available)`);
					return prev;
				}
					*/
				return prev.map((c) =>
					c.store_item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c,
				);
			}
			if (item.quantity_on_hand < 1) {
				setLookupError(`"${item.title}" is out of stock`);
				return prev;
			}
			return [
				...prev,
				{
					store_item_id: item.id,
					title: item.title,
					price_cents: item.price_cents,
					quantity: 1,
					quantity_on_hand: item.quantity_on_hand,
				},
			];
		});
	};

	const updateQuantity = (store_item_id, delta) => {
		setCart((prev) =>
			prev
				.map((c) =>
					c.store_item_id === store_item_id
						? { ...c, quantity: c.quantity + delta }
						: c,
				)
				.filter((c) => c.quantity > 0),
		);
	};

	const removeFromCart = (store_item_id) => {
		setCart((prev) => prev.filter((c) => c.store_item_id !== store_item_id));
	};

	// Undo the last camera-scanned item: decrement qty by 1, or remove if qty was 1
	const undoLastScanned = () => {
		if (!lastScannedItem) return;
		setCart((prev) => {
			const existing = prev.find(
				(c) => c.store_item_id === lastScannedItem.store_item_id,
			);
			if (!existing) return prev;
			if (existing.quantity <= 1) {
				return prev.filter(
					(c) => c.store_item_id !== lastScannedItem.store_item_id,
				);
			}
			return prev.map((c) =>
				c.store_item_id === lastScannedItem.store_item_id
					? { ...c, quantity: c.quantity - 1 }
					: c,
			);
		});
		setLastScannedItem(null);
	};

	const totalCents = cart.reduce(
		(sum, c) => sum + c.price_cents * c.quantity,
		0,
	);

	const handleManualSearch = async () => {
		if (!searchQuery.trim()) return;
		setSearching(true);
		const { data } = await supabase
			.from("store_items")
			.select("id, title, price_cents, quantity_on_hand, sku, barcode")
			.eq("active", true)
			.ilike("title", `%${searchQuery}%`)
			.limit(10);
		setSearchResults(data || []);
		setSearching(false);
	};

	const completeSale = async () => {
		setCompleting(true);
		setConfirmOpen(false);
		try {
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) throw new Error("Not authenticated");
			const sessionAuthHeader = "Bearer " + session.access_token;

			const res = await fetch(POS_COMPLETE_API, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: sessionAuthHeader,
				},
				body: JSON.stringify({
					items: cart.map((c) => ({
						store_item_id: c.store_item_id,
						quantity: c.quantity,
						price_cents: c.price_cents,
					})),
					payment_method: paymentMethod,
				}),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.error || "Sale failed");

			setLastSaleId(data.sale_id);
			setCart([]);
			setLastScannedItem(null);
			setSuccessSnack(true);
		} catch (err) {
			setErrorSnack(err.message);
		}
		setCompleting(false);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: { xs: "column", md: "row" },
				gap: 2,
				p: 2,
				minHeight: "80vh",
			}}
		>
			{/* Left panel – scanner + search */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography variant="h5" gutterBottom>
					Point of Sale
				</Typography>

				{/* Barcode scanner */}
				<Paper sx={{ p: 2, mb: 2 }}>
					<Typography variant="subtitle2" gutterBottom>
						Scan Barcode
					</Typography>
					<BarcodeScanner
						onScan={lookupBarcode}
						disabled={lookupLoading}
						lastScannedTitle={lastScannedItem?.title || null}
						lastScanKey={scanKey}
						onUndo={lastScannedItem ? undoLastScanned : null}
						onDialogClose={() => setLastScannedItem(null)}
					/>
					{lookupLoading && <CircularProgress size={20} sx={{ mt: 1 }} />}
					{lookupError && (
						<Alert
							severity="warning"
							sx={{ mt: 1 }}
							onClose={() => setLookupError("")}
						>
							{lookupError}
						</Alert>
					)}
				</Paper>

				{/* Manual product search */}
				<Paper sx={{ p: 2 }}>
					<Typography variant="subtitle2" gutterBottom>
						Manual Search
					</Typography>
					<Box display="flex" gap={1}>
						<TextField
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={(e) => e.key === "Enter" && handleManualSearch()}
							placeholder="Search by product name…"
							size="small"
							fullWidth
						/>
						<Button
							variant="outlined"
							size="small"
							onClick={handleManualSearch}
							disabled={searching}
						>
							Search
						</Button>
					</Box>
					{searching && <CircularProgress size={20} sx={{ mt: 1 }} />}
					{searchResults.length > 0 && (
						<Box mt={1}>
							{searchResults.map((item) => (
								<Box
									key={item.id}
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									sx={{
										py: 0.5,
										borderBottom: "1px solid",
										borderColor: "divider",
										cursor: item.quantity_on_hand > 0 ? "pointer" : "default",
										opacity: item.quantity_on_hand > 0 ? 1 : 0.5,
									}}
									onClick={() => item.quantity_on_hand > 0 && addToCart(item)}
								>
									<Box>
										<Typography variant="body2">{item.title}</Typography>
										<Typography variant="caption" color="text.secondary">
											SKU: {item.sku || "—"} · Stock: {item.quantity_on_hand}
										</Typography>
									</Box>
									<Box textAlign="right">
										<Typography variant="body2">
											${(item.price_cents / 100).toFixed(2)}
										</Typography>
										{item.quantity_on_hand === 0 && (
											<Chip label="Out of stock" size="small" color="error" />
										)}
									</Box>
								</Box>
							))}
						</Box>
					)}
				</Paper>
			</Box>

			{/* Right panel – cart */}
			<Box sx={{ width: { xs: "100%", md: 380 }, flexShrink: 0 }}>
				<Paper sx={{ p: 2, position: { md: "sticky" }, top: { md: 80 } }}>
					<Typography variant="h6" gutterBottom>
						Cart ({cart.length} {cart.length === 1 ? "item" : "items"})
					</Typography>

					{cart.length === 0 ? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ py: 2, textAlign: "center" }}
						>
							Scan or search to add items
						</Typography>
					) : (
						<>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Item</TableCell>
										<TableCell align="center">Qty</TableCell>
										<TableCell align="right">Price</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{cart.map((item) => (
										<TableRow key={item.store_item_id}>
											<TableCell>
												<Typography variant="body2">{item.title}</Typography>
												<Typography variant="caption" color="text.secondary">
													${(item.price_cents / 100).toFixed(2)} ea
												</Typography>
											</TableCell>
											<TableCell align="center">
												<Box
													display="flex"
													alignItems="center"
													justifyContent="center"
													gap={0.5}
												>
													<IconButton
														size="small"
														onClick={() =>
															updateQuantity(item.store_item_id, -1)
														}
													>
														<Remove fontSize="small" />
													</IconButton>
													<Typography variant="body2">
														{item.quantity}
													</Typography>
													<IconButton
														size="small"
														onClick={() =>
															updateQuantity(item.store_item_id, 1)
														}
													>
														<Add fontSize="small" />
													</IconButton>
												</Box>
											</TableCell>
											<TableCell align="right">
												${((item.price_cents * item.quantity) / 100).toFixed(2)}
											</TableCell>
											<TableCell>
												<IconButton
													size="small"
													onClick={() => removeFromCart(item.store_item_id)}
												>
													<Delete fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>

							<Divider sx={{ my: 1 }} />
							<Box display="flex" justifyContent="space-between" mb={1}>
								<Typography variant="h6">Total</Typography>
								<Typography variant="h6">
									${(totalCents / 100).toFixed(2)}
								</Typography>
							</Box>

							<Box mb={2}>
								<Typography variant="subtitle2" gutterBottom>
									Payment Method
								</Typography>
								<Select
									value={paymentMethod}
									onChange={(e) => setPaymentMethod(e.target.value)}
									size="small"
									fullWidth
								>
									<MenuItem value="cash">Cash</MenuItem>
									<MenuItem value="card">Card</MenuItem>
									<MenuItem value="other">Other</MenuItem>
								</Select>
							</Box>

							<Button
								variant="contained"
								color="success"
								fullWidth
								size="large"
								startIcon={<CheckCircle />}
								onClick={() => setConfirmOpen(true)}
								disabled={completing}
							>
								{completing ? <CircularProgress size={20} /> : "Complete Sale"}
							</Button>

							<Button
								variant="outlined"
								color="error"
								fullWidth
								size="small"
								sx={{ mt: 1 }}
								onClick={() => {
									setCart([]);
									setLastScannedItem(null);
								}}
								disabled={completing}
							>
								Clear Cart
							</Button>
						</>
					)}
				</Paper>
			</Box>

			{/* Confirm dialog */}
			<Dialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Confirm Sale</DialogTitle>
				<DialogContent>
					<Typography>
						Total: <strong>${(totalCents / 100).toFixed(2)}</strong>
					</Typography>
					<Typography>Payment: {paymentMethod}</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						{cart.length} item(s) will be deducted from inventory.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
					<Button onClick={completeSale} variant="contained" color="success">
						Confirm
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbars */}
			<Snackbar
				open={successSnack}
				autoHideDuration={4000}
				onClose={() => setSuccessSnack(false)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setSuccessSnack(false)}>
					Sale complete! {lastSaleId && `(ID: ${lastSaleId.slice(0, 8)}…)`}
				</Alert>
			</Snackbar>
			<Snackbar
				open={!!errorSnack}
				autoHideDuration={5000}
				onClose={() => setErrorSnack("")}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert severity="error" onClose={() => setErrorSnack("")}>
					{errorSnack}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default POS;
